import * as styles from "./DogListingPage.module.scss";

import qs from "querystring";
import SSRSuspense from "@dogstrust/dt-web-core-lib-front-end/src/components/SSRSuspense/SSRSuspense";
import SEO from "@dogstrust/dt-web-core-lib-front-end/src/components/seo";
import DogListingsFilters from "@dogstrust/dt-web-core-lib-front-end/src/containers/filters/DogListingsFilters";
import IntroRehomingListings from "@dogstrust/dt-web-core-lib-front-end/src/containers/introBlocks/IntroRehomingListings";
import {
	useDogSearchApi,
	usePageManager,
} from "@dogstrust/dt-web-core-lib-front-end/src/context";
import { Layout } from "@dogstrust/uk/components/Layout/Layout";

import { getSeoProps } from "@dogstrust/dt-web-core-lib-front-end/src/utils/seo";

import { Head as DefaultHead } from "@dogstrust/dt-web-core-lib-front-end/src/components/Head/Head";
import React, { lazy, useEffect } from "react";
import { useState } from "react";

const FilterPanel = lazy(
	() =>
		import(
			"@dogstrust/dt-web-core-lib-front-end/src/containers/filters/FilterPanel"
		),
);

export interface DogIndexPageProps {
	data: Queries.DogListingPageQuery;
}

export const Head: React.FC<DogIndexPageProps> = ({
	data: {
		pageData: { field_meta_tags, field_hreflang_url },
	},
}) => {
	return (
		<DefaultHead pageUrl={"/rehoming/dogs"} hrefLangUrl={field_hreflang_url}>
			<SEO
				{...getSeoProps(field_meta_tags, {
					title: "Dogs seeking a forever home | Dogs Trust",
					description: "Dogs seeking a forever home",
					canonical_url: `${process.env.GATSBY_DOMAIN_NAME}/rehoming/dogs`,
					og_type: "article",
					og_url: `${process.env.GATSBY_DOMAIN_NAME}/rehoming/dogs`,
					og_title: "Dogs seeking a forever home | Dogs Trust",
					og_description: "Dogs seeking a forever home",
					twitter_cards_type: "summary",
					twitter_cards_description: "Dogs seeking a forever home.",
					twitter_cards_site: "@DogsTrust",
					twitter_cards_title: "Dogs seeking a forever home | Dogs Trust",
					twitter_cards_page_url: `${process.env.GATSBY_DOMAIN_NAME}/rehoming/dogs`,
				})}
			/>
		</DefaultHead>
	);
};

const DogListingPage: React.FC<DogIndexPageProps> = ({
	data: {
		allNodeCentre: { nodes: allCentres },
		pageData: { breadcrumbs },
	},
}) => {
	const { setContentWidth, deregisterPage, loadPagination, usePagination } =
		usePageManager();
	useEffect(() => {
		usePagination(false);
		setContentWidth("DEFAULT");
		return () => deregisterPage();
	}, [setContentWidth, usePagination, deregisterPage]);

	const [firstLoad, setFirstLoad] = useState(true);
	const {
		results,
		resultsCount,
		resultsUrls,
		onShowDogs,
		searchListingsUrl,
		queryStrings,
	} = useDogSearchApi();

	useEffect(() => {
		// We need to ensure that the pagination is reset to page 0 when the filters are changed
		// To avoid the scenario where the listing page is visited starting on page n

		const { page: _page, ...queryStringsWithoutPage } = qs.parse(
			queryStrings.replace("?", ""),
		);
		const pageZeroQueryStrings = qs.stringify({
			page: 0,
			...queryStringsWithoutPage,
		});
		loadPagination(
			resultsUrls,
			resultsCount,
			`${searchListingsUrl.split("?")[0]}?${pageZeroQueryStrings}`,
		);
	}, [loadPagination, results, resultsCount, searchListingsUrl, queryStrings]);

	useEffect(() => {
		if (firstLoad) {
			onShowDogs();
			setFirstLoad(false);
		}
	}, [onShowDogs, firstLoad]);

	return (
		<Layout breadcrumbs={breadcrumbs ? [...breadcrumbs] : []}>
			<div className={styles.listingssection}>
				<IntroRehomingListings
					title={"Dogs seeking a forever home"}
					body={`<p><b>${resultsCount}</b> matching dogs</p>`}
				/>
				<DogListingsFilters />
			</div>
			<SSRSuspense>
				<FilterPanel
					allCentres={[...allCentres]}
					shouldMaintainFilters={true}
					isListingPage={true}
				/>
			</SSRSuspense>
		</Layout>
	);
};
export default DogListingPage;
